import { groupBy } from "./linq";
import { intl } from "../containers/app";
import { handleError } from "./errorLogger";
import { createQueryString } from "./crudOperations";
import { get, checkHttpStatus, checkResponseGet } from "./httpFunctions";

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

/**
 * pravidla pro atributy zásilky
 */
type Rules = {
  colliMinLength?: number;
  colliMinWidth?: number;
  colliMinHeight?: number;
  colliLength: number;
  colliWidth: number;
  colliHeight: number;
  colliWeight: number;
  colliMinWeight?: number;
  colliCalculatedWeight?: number;
  colliLoadingMeters?: number;
  count: number;
  weight: number;
  calculatedWeight?: number;
  volume: number;
  loadingMeters?: number;
  wrappings?: any;
  cashOnDeliveryAmount: number;
};

/**
 * regexp pro validaci ve formu
 */
class Pattern {
  public pattern: string;
  public message: string;

  constructor(pattern: string, message: string) {
    this.pattern = pattern;
    this.message = message;
  }
}

/**
 * rozpočítá váhu zásilky mezi kusy
 * @param weight celková váha
 * @param count počet kusů
 * @param type typ rozpočítání váhy
 */
export function setWeight(
  weight: number,
  count: number,
  type: number
): number[] {
  let calculated = weight,
    firstWeight = weight;
  if (type === 2) {
    const result = weight / count;
    firstWeight = calculated = result;
    const remainder = firstWeight % 1;
    if (remainder > 0) {
      calculated = result >> 0;
      calculated = calculated === 0 ? 1 : calculated;
      firstWeight = Math.ceil(
        roundNumber(firstWeight + remainder * (count - 1), 5)
      );
    }
  }
  const weights = [firstWeight];
  for (let i = 1; i < count; i++) {
    weights.push(calculated);
  }
  return weights;
}

/**
 * sečte množství všech ADR kusů
 * @param adrs kolekce ADR kusů
 */
export function sumAdrQuantity(adrs: any[], submit = false): number {
  return sum(
    submit && adrs ? adrs.map((quantity) => ({ quantity })) : adrs,
    "quantity"
  );
}

/**
 * sečte celkovou váhu všech ADR kusů
 * @param adrs kolekce ADR kusů
 */
export function sumAdrGrossWeight(adrs: any[], submit = false): number {
  return sum(
    submit && adrs ? adrs.map((grossWeight) => ({ grossWeight })) : adrs,
    "grossWeight"
  );
}

/**
 * sečte počet manipulačních jednotek všech ADR kusů
 * @param adrs kolekce ADR kusů
 */
export function sumAdrHandlingUnitsQuantity(
  adrs: any[],
  submit = false
): number {
  return sum(
    submit && adrs
      ? adrs.map((handlingUnitsQuantity) => ({ handlingUnitsQuantity }))
      : adrs,
    "handlingUnitsQuantity"
  );
}

/**
 * sečte počet kusů v seznamu zásilek
 * @param consignments seznam zásilek
 */
export function sumConsignmentCollis(consignments: any[]): number {
  return consignments.map((c) => c.collis.length).reduce((a, b) => a + b, 0);
}

export function getMaxLdmRule(rules: any): any {
  return rules
    ? rules.loadingMeters ??
        (rules.weight && roundNumber(rules.weight / 1650, 3))
    : undefined;
}

export function isLdmConsignment(
  product: any,
  collis: any[],
  adrCount: number
): boolean {
  const isAdr = adrCount > 0;
  return (
    product &&
    (product.code === "ECI" ||
      product.code === "SPI" ||
      product.code === "ECI_B2C" ||
      product.code === "ERI" ||
      product.code === "PPI") &&
    collis.some((c) => isLdmItem(c, isAdr))
  );
}

export function isLdmItem(item: any, adr: boolean): boolean {
  return item.stackable ? item.height >= 200 : item.height > 100 || adr;
}

/**
 * sečte celkovou váhu všech kusů v kg
 * @param collis kolekce kusů
 * @param onlyNonStackable pouze kusy, které nejsou stohovatelné
 */
export function sumCollisWeight(
  collis: any[],
  onlyNonStackable = false
): number {
  return sum(
    onlyNonStackable && collis ? collis.filter((c) => !c.stackable) : collis,
    "weight"
  );
}

const sum = (array: any[], prop: string) =>
  array ? array.map((c) => c[prop] || 0).reduce((a, b) => a + b, 0) : 0;

/**
 * spocita kusy, ktere jsou nestohovatelne
 * @param collis kolekce kusů
 */
export function sumNotStackableCollis(collis: any[]): number {
  return collis.filter((c) => !c.stackable).length;
}

/**
 * vypočítá celkový objem všech kusů v m3
 * @param collis kolekce kusů
 */
export function sumCollisVolume(collis: any[]): number {
  return roundNumber(
    collis
      .filter((c) => c.length && c.width && c.height)
      .map((c) => getVolume(c.length, c.width, c.height))
      .reduce((a, b) => a + b, 0),
    3
  );
}

export function sumCollisLoadingMeters(collis: any[], adr: boolean): number {
  return roundNumber(
    collis
      .filter((c) => c.length && c.width && c.height)
      .map((c) => getLoadingMeters(c, adr))
      .reduce((a, b) => a + b, 0),
    3
  );
}

export function sumCollisCalculatedWeight(collis: any[], adr: boolean): number {
  return roundNumber(
    collis
      .filter((c) => c.length && c.width && c.height)
      .map((c) => getCalculatedWeight(c, adr))
      .reduce((a, b) => a + b, 0),
    2
  );
}

export const getConsignmentType = (consignment: any): string => {
  const groupped = groupConsignmentsCollisByWrapping([consignment]);
  return groupped.length === 0
    ? "--"
    : groupped.length > 1
    ? "mixed"
    : groupped[0][0];
};

export const getVolume = (
  length: number,
  width: number,
  height: number,
  countOfItems = 1
): number => ((length * width * height) / 1000000) * countOfItems;

export const getCalculatedWeight = (
  item: any[],
  adr: boolean,
  countOfItems = 1
): number => getLoadingMeters(item, adr, countOfItems) * 1650;

export const getLoadingMeters = (
  item: any,
  adr: boolean,
  countOfItems = 1
): number =>
  countOfItems *
  (isLdmItem(item, adr)
    ? (item.length * item.width) / 10000 / 2.4
    : (getVolume(item.length, item.width, item.height) * 250) / 1650);

export const roundNumber = (number: number, decimals = 2): number => {
  const n = Math.pow(10, decimals);
  return Math.round((number + Number.EPSILON) * n) / n;
};

/**
 * vrátí slovník typů obalu a jejich počtu v rámci seznamu zásilek
 * @param consignments seznam zásilek
 */
export function groupConsignmentsCollisByWrapping(
  consignments: any[]
): Array<[string, number]> {
  const dictionary = new Map<string, number>();
  consignments.forEach((c) =>
    c.collis
      .filter((c: any) => c.wrapping)
      .forEach((cl: any) => {
        const previousValue = dictionary.get(cl.wrapping.name);
        dictionary.set(cl.wrapping.name, previousValue ? previousValue + 1 : 1);
      })
  );
  return [...dictionary];
}

export const invalidForSelect = (consignment: any): boolean =>
  !consignment.valid || consignment.state === "CP" || consignment.cancelled;

export function loadClientRules(
  product: any,
  carryToFloor: boolean,
  setRules: any,
  onSuccess: any,
  pickupCountryId: any,
  deliveryCountryId: any
): void {
  if (product) {
    get(
      `product/${product.id}/product-rule${createQueryString({
        pickupCountryId,
        deliveryCountryId,
      })}`
    )
      .then((r) => {
        if (checkHttpStatus(r) && checkResponseGet(r)) {
          setRules(updateRules(r.data.data, carryToFloor));
          onSuccess && onSuccess();
        } else {
          setRules(createRules(product.code, carryToFloor));
        }
      })
      .catch((err) => {
        setRules(createRules(product.code, carryToFloor));
        handleError(err);
      });
  } else {
    setRules(createRules("ECD_B2B", false));
  }
}

const updateRules = (rules: any, carryToFloor: boolean) =>
  rules && rules !== "e"
    ? {
        ...rules,
        weight: carryToFloor ? rules.weightCarry : rules.weight,
        colliWeight: carryToFloor ? rules.colliWeightCarry : rules.colliWeight,
      }
    : rules;

/**
 * vytvoří pravidla pro vlastnosti zásilek dle produktu
 * @param productCode produkt (ECD_B2C, ECD_B2B..)
 * @param carryToFloor jestli je výnos do patra
 * @param values kolekce pravidel
 */
function createRules(
  productCode: string,
  carryToFloor?: boolean,
  values?: any[]
): Rules {
  if (productCode === "ECD_B2B") {
    return {
      colliLength: getRuleValue("ColliLength", 400, values),
      colliWidth: getRuleValue("ColliWidth", 220, values),
      colliHeight: getRuleValue("ColliHeight", 220, values),
      colliWeight: getRuleValue("ColliWeight", 800, values),
      count: getRuleValue("Count", 100, values),
      weight: getRuleValue("Weight", 3000, values),
      volume: getRuleValue("Volume", 12, values),
      cashOnDeliveryAmount: getRuleValue("CodAmount", 200000, values),
    };
  } else if (productCode === "ECI" || productCode === "ECI_B2C") {
    return {
      colliLength: getRuleValue("ColliLength", 400, values),
      colliWidth: getRuleValue("ColliWidth", 220, values),
      colliHeight: getRuleValue("ColliHeight", 220, values),
      colliWeight: getRuleValue("ColliWeight", 1500, values),
      count: getRuleValue("Count", 10, values),
      weight: getRuleValue("Weight", 2500, values),
      volume: getRuleValue("Volume", 10, values),
      cashOnDeliveryAmount: getRuleValue("CodAmount", 200000, values),
    };
  } else if (productCode === "ERD" || productCode === "ERI") {
    return {
      colliLength: getRuleValue("ColliLength", 240, values),
      colliWidth: getRuleValue("ColliWidth", 120, values),
      colliHeight: getRuleValue("ColliHeight", 220, values),
      colliWeight: getRuleValue("ColliWeight", 1000, values),
      count: getRuleValue("Count", 10, values),
      weight: getRuleValue("Weight", 2500, values),
      volume: getRuleValue("Volume", 10, values),
      cashOnDeliveryAmount: getRuleValue("CodAmount", 200000, values),
    };
  } else if (productCode === "ELD" || productCode === "ELI") {
    return {
      colliLength: getRuleValue("ColliLength", 1350, values),
      colliWidth: getRuleValue("ColliWidth", 240, values),
      colliHeight: getRuleValue("ColliHeight", 300, values),
      colliWeight: getRuleValue("ColliWeight", 25000, values),
      count: getRuleValue("Count", 999, values), //není
      weight: getRuleValue("Weight", 25000, values),
      volume: getRuleValue("Volume", 999, values), //není - 97.2
      cashOnDeliveryAmount: getRuleValue("CodAmount", 200000, values),
    };
  } else if (productCode === "SPI" || productCode === "PPI") {
    return {
      colliMinLength: getRuleValue("ColliMinLength", 80, values),
      colliMinWidth: getRuleValue("ColliMinWidth", 60, values),
      colliMinHeight: getRuleValue("ColliMinHeight", 3, values),
      colliLength: getRuleValue("ColliLength", 120, values),
      colliWidth: getRuleValue("ColliWidth", 100, values),
      colliHeight: getRuleValue("ColliHeight", 220, values),
      colliWeight: getRuleValue("ColliWeight", 750, values),
      count: getRuleValue("Count", 10, values),
      weight: getRuleValue("Weight", 2500, values),
      calculatedWeight: getRuleValue("CalculatedWeight", 2640, values),
      volume: getRuleValue("Volume", 10.56, values),
      loadingMeters: getRuleValue("LoadingMeters", 2, values),
      wrappings: getRuleStringValue(
        "Wrappings",
        {
          PALLET_80x60: 10,
          PALLET_80x60_POOL: 10,
          PALLET_80x60_FRAME: 10,
          PALLET_120x80: 5,
          EURO_PALLET: 5,
          PALLET_120x100: 4,
        },
        values
      ),
      cashOnDeliveryAmount: getRuleValue("CodAmount", 200000, values),
    };
  } else {
    //ECD_B2C a jiné
    return {
      colliLength: getRuleValue("ColliLength", 400, values),
      colliWidth: getRuleValue("ColliWidth", 220, values),
      colliHeight: getRuleValue("ColliHeight", 220, values),
      colliWeight: getRuleValue(
        carryToFloor ? "ColliWeightCarry" : "ColliWeight",
        carryToFloor ? 50 : 800,
        values
      ),
      count: getRuleValue("Count", 100, values),
      weight: getRuleValue(
        carryToFloor ? "WeightCarry" : "Weight",
        carryToFloor ? 80 : 3000,
        values
      ),
      volume: getRuleValue("Volume", 12, values),
      cashOnDeliveryAmount: getRuleValue("CodAmount", 200000, values),
    };
  }
}

export const groupCollisByWrapping = (collis: any[], wrappings: any[]): any =>
  groupBy(
    collis,
    (c: any) =>
      c.wrapping?.code ||
      wrappings?.find((w) => w.id === c.wrappingId)?.code ||
      "--"
  );

export function checkWrappings(
  collis: any[],
  wrappings: any[],
  rules: Rules
): boolean {
  if (rules && rules.wrappings && collis) {
    const groups = groupCollisByWrapping(collis, wrappings);
    return Object.keys(rules.wrappings).every((w) => {
      const group = groups.find((g: any) => g[0] === w);
      return (
        !group || !rules.wrappings[w] || group[1].length < rules.wrappings[w]
      );
    });
  }
  return true;
}

/**
 * zkontroluje kusy, jestli maji spravne rozmery
 * @param collis pole kusu
 * @param rules pravidla pro kusy
 */
export function checkCollisForRules(
  collis: any[],
  rules: Rules,
  adr: boolean
): boolean {
  const noRules = Object.keys(rules).length === 0;
  return collis.every(
    (c) =>
      c.height > 0 &&
      c.width > 0 &&
      c.length > 0 &&
      c.weight > 0 &&
      (noRules ||
        (c.height <= rules.colliHeight &&
          (!rules.colliMinHeight || c.height >= rules.colliMinHeight) &&
          c.width <= rules.colliWidth &&
          (!rules.colliMinWidth || c.width >= rules.colliMinWidth) &&
          c.length <= rules.colliLength &&
          (!rules.colliMinLength || c.length >= rules.colliMinLength) &&
          c.weight <= rules.colliWeight &&
          (!rules.colliMinWeight || c.weight >= rules.colliMinWeight) &&
          validCalculatedWeight(c, rules, adr) &&
          validLoadingMeters(c, rules, adr)))
  );
}

export const validCalculatedWeight = (
  colli: any,
  rules: Rules,
  adr: boolean
): boolean =>
  !rules.colliCalculatedWeight ||
  getCalculatedWeight(colli, adr) <= rules.colliCalculatedWeight;

export const validLoadingMeters = (
  colli: any,
  rules: Rules,
  adr: boolean
): boolean =>
  !rules.colliLoadingMeters ||
  getLoadingMeters(colli, adr) <= rules.colliLoadingMeters;

/**
 * vrátí regexp pro format telefonního čísla dané země - https://cs.qaz.wiki/wiki/National_conventions_for_writing_telephone_numbers
 * @param countryCode kod země (CZ..)
 */
export function setPhoneNumberPattern(countryCode: string): Pattern {
  switch (countryCode) {
    case "DK":
    case "NO":
    case "CY":
    case "BH":
    case "MD":
    case "BA":
      return new Pattern(
        "^\\d{8}$",
        intl.formatMessage({
          id: "address.validation.phone.eight",
          defaultMessage: "Zadejte telefonní číslo o délce 8!",
        })
      );
    case "CZ":
    case "SK":
    case "HU":
    case "PL":
    case "FR":
    case "PT":
      return new Pattern(
        "^\\d{9}$",
        intl.formatMessage({
          id: "address.validation.phone.nine",
          defaultMessage: "Zadejte telefonní číslo v devítimístném formátu!",
        })
      );
    case "GB":
    case "GR":
    case "RU":
      return new Pattern(
        "^\\d{10}$",
        intl.formatMessage({
          id: "address.validation.phone.ten",
          defaultMessage: "Zadejte telefonní číslo o délce 10!",
        })
      );
    case "ES":
      return new Pattern(
        "^\\d{6,9}$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
    case "SE":
      return new Pattern(
        "^\\d{8,10}$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
    case "IE":
      return new Pattern(
        "^\\d{6,11}$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
    case "BE":
    case "NL":
    case "RO":
    case "CH":
      return new Pattern(
        "^\\d{9,10}$",
        intl.formatMessage({
          id: "address.validation.phone.nineTen",
          defaultMessage: "Zadejte telefonní číslo o délce 9 nebo 10!",
        })
      );
    case "IT":
      return new Pattern(
        "^\\d{3,12}$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
    case "DE":
      return new Pattern(
        "^\\d{6,12}$",
        intl.formatMessage({
          id: "address.validation.phone.eleven",
          defaultMessage: "Zadejte telefonní číslo o délce 6 až 12!",
        })
      );
    case "AT":
      return new Pattern(
        "^\\d{5,14}$",
        intl.formatMessage({
          id: "address.validation.phone.tenMore",
          defaultMessage: "Zadejte telefonní číslo o délce 5 až 12!",
        })
      );
    default:
      return new Pattern(
        "^\\d+$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
  }
}

/**
 * vrátí regexp pro format PSČ dané země
 * @param countryCode kod země (CZ..)
 */
export function setZipCodePattern(countryCode: string): Pattern {
  switch (countryCode) {
    case "CZ":
    case "SK":
    case "EE":
    case "ES":
    case "FR":
    case "IT":
    case "GR":
    case "SA":
    case "KW":
    case "JO":
    case "MA":
    case "RS":
    case "TR":
    case "UA":
    case "BA":
      return new Pattern(
        "^\\d{5}$",
        intl.formatMessage({
          id: "address.validation.zipCode.five",
          defaultMessage: "Zadejte PSČ v pětimístném formátu!",
        })
      );
    case "PL":
      return new Pattern(
        "^\\d{2}-?\\d{3}$",
        intl.formatMessage({
          id: "address.validation.zipCode.poland",
          defaultMessage: "Zadejte PSČ ve správném formátu (XX-XXX)!",
        })
      );
    case "AT":
    case "HU":
    case "BE":
    case "BG":
    case "DK":
    case "CY":
    case "GE":
    case "MK":
    case "TN":
      return new Pattern(
        "^\\d{4}$",
        intl.formatMessage({
          id: "address.validation.zipCode.four",
          defaultMessage: "Zadejte PSČ v čtyřmístném formátu!",
        })
      );
    case "BH":
    case "OM":
      return new Pattern(
        "^\\d{3}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "HR":
    case "LT":
    case "FI":
    case "SE":
      return new Pattern(
        `^(${countryCode}-)?\\d{5}$`,
        intl.formatMessage({
          id: "address.validation.zipCode.fiveCountry",
          defaultMessage:
            "Zadejte PSČ v pětimístném formátu s prefixem kódu státu a pomlčkou (XX-)!",
        })
      );
    case "LV":
    case "SI":
    case "MD":
      return new Pattern(
        `^(${countryCode}-)?\\d{4}$`,
        intl.formatMessage({
          id: "address.validation.zipCode.fourCountry",
          defaultMessage:
            "Zadejte PSČ v čtyřmístném formátu s prefixem kódu státu a pomlčkou (XX-)!",
        })
      );
    case "LU":
      return new Pattern(
        `^(L-)?\\d{4}$`,
        intl.formatMessage({
          id: "address.validation.zipCode.four",
          defaultMessage: "Zadejte PSČ v čtyřmístném formátu!",
        })
      );
    case "RO":
    case "RU":
    case "KZ":
      return new Pattern(
        "^\\d{6}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "NL":
      return new Pattern(
        "^\\d{4}((-| )[A-Z]{2})?$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "PT":
      return new Pattern(
        "^\\d{4}(-\\d{3})?$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "MT":
      return new Pattern(
        "^[A-Za-z0-9 -]{0,15}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "IE":
      return new Pattern(
        "^[A-Za-z0-9 -]{0,15}$",
        //"(?:^[AC-FHKNPRTV-Y][0-9]{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Y]{4}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "US":
      return new Pattern(
        "^\\d{5}(?:[-\\s]\\d{4})?$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "GB":
      return new Pattern(
        "^[A-Za-z0-9 -]{0,15}$",
        //"^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "DE":
      return new Pattern(
        "^(?!01000|99999)(0[1-9]\\d{3}|[1-9]\\d{4})$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "AE":
    case "YE":
    case "SY":
      return new Pattern(
        "^[A-Z]{2}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "QA":
    case "LB":
      return new Pattern(
        "^\\d{8}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    default:
      return ["GI"].includes(countryCode)
        ? new Pattern(
            "^[A-Za-z0-9 ]+$",
            intl.formatMessage({
              id: "address.validation.zipCode.alphanumeric",
              defaultMessage: "PSČ musí být alfanumerické!",
            })
          )
        : new Pattern(
            "^\\d+$",
            intl.formatMessage({
              id: "address.validation.zipCode.others",
              defaultMessage:
                "Zadejte PSČ v korektním formátu pro vybranou zemi!",
            })
          );
  }
}

/**
 * zpracuje klienta
 * @param client klient
 */
export function parseClient(client: any): string {
  return client
    ? `${client.name || "--"} (${client.code || "WA"} / ${
        client.evoCode || "--"
      })`
    : "";
}

/**
 * zpracuje adresu
 * @param address adresa ke zpracovaní
 */
export function parseAddress(address: any): string {
  return `${address.name || "--"}, ${address.street || ""} ${
    address.streetNumber || ""
  }, ${address.city || ""} ${address.zipCode || ""} ${
    (address.country && address.country.code) || "CZ"
  }`;
}

/**
 * zpracuje ulici
 * @param street ulice
 * @param streetNumber číslo popisné
 */
export function parseStreet(address: any): string {
  return address
    ? `${address.street || "--"} ${address.streetNumber || ""}`
    : "";
}

/**
 * zpracuje částku dobírky/faktury a přidá měnu
 * @param cashOnDelivery dobírka/faktura ke zpracování
 */
export function parseItemAmount(cashOnDelivery: any): string {
  return cashOnDelivery
    ? `${cashOnDelivery.amount} ${
        (cashOnDelivery.currency && cashOnDelivery.currency.code) || "CZK"
      }`
    : "--";
}

/**
 * zpracuje variabilní symbol dobírky
 * @param cashOnDelivery dobírka ke zpracování
 */
export function parseCashOnDeliveryVariableSymbol(cashOnDelivery: any): string {
  return cashOnDelivery ? `VS:${cashOnDelivery.variableSymbol}` : "--";
}

/**
 * zpracuje dobírku
 * @param cashOnDelivery dobírka ke zpracování
 */
export function parseCashOnDelivery(cashOnDelivery: any): string {
  return cashOnDelivery
    ? `${parseItemAmount(cashOnDelivery)} / ${parseCashOnDeliveryVariableSymbol(
        cashOnDelivery
      )}`
    : "--";
}

/**
 * zpracuje telefon z adresy
 * @param address adresa
 */
export function parsePhone(address: any): string {
  return address && address.preselection && address.contactPhone
    ? `${address.preselection.code}${address.contactPhone}`
    : "--";
}

/**
 * zpracuje datum do formátu d.m.yyyy
 * @param dateTime datum
 */
export function parseDate(dateTime: string): string {
  return dateTime ? intl.formatDate(dateTime) : "--";
}

/**
 * zpracuje datum a čas, pokud není zadaný, tak vrátí aktuální
 * @param dateTime datum a čas
 */
export function parseDateTime(dateTime?: string): string {
  return dateTime
    ? `${intl.formatDate(dateTime)} ${intl.formatTime(dateTime)}`
    : "--";
}

/**
 * vrátí hodnotu pravidla z kolekce pravidel
 * @param type druh pravidla
 * @param defaultValue základní hodnota
 * @param values kolekce pravidel
 */
function getRuleValue(
  type: string,
  defaultValue: number,
  values?: any[]
): number {
  if (values) {
    const rules = values.filter((r) => r.propertyName === type);
    return (rules.length > 0 && rules[0].value) || defaultValue;
  } else {
    return defaultValue;
  }
}

function getRuleStringValue(
  type: string,
  defaultValue: any,
  values?: any[]
): number {
  if (values) {
    const rules = values.filter((r) => r.propertyName === type);
    return (rules.length > 0 && rules[0].stringValue) || defaultValue;
  } else {
    return defaultValue;
  }
}

/**
 *
 * @param value string
 * @param maxLength maximalni delka
 * @returns orezena hodnota
 */
export const cutValue = (value: string, maxLength: number): string => {
  const cutValue = value ? value.split("\n")[0] : "";
  return cutValue.substring(0, Math.min(cutValue.length, maxLength));
};

export const integerSorter = (a: number, b: number): number =>
  a > b ? -1 : a < b ? 1 : 0;

export const canBeCod = (
  country: any,
  secondCountry: any,
  pickup: boolean
): boolean =>
  country &&
  secondCountry &&
  (country[`codCountries${pickup ? "Pickup" : "Delivery"}`].includes(null) ||
    country[`codCountries${pickup ? "Pickup" : "Delivery"}`].includes(
      secondCountry.id
    ));
